import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Lock, Mail, ArrowUpRight, Check } from "lucide-react";

const DEFAULT_PACKAGE = "p100";

/**
 * Preview / coming-soon TopUp page.
 *
 * Payments are intentionally disabled site-wide until our payment provider
 * onboarding is complete. The CTA falls back to a contact-sales mailto so
 * customers can still reach us for early access, custom invoicing, or wire
 * transfer.
 */
export default function TopUp() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(DEFAULT_PACKAGE);
  const [bal, setBal] = useState(null);

  useEffect(() => {
    api.get("/balance/topup/packages").then((r) => {
      setPackages(r.data.packages || []);
    });
    if (user && user.id) {
      api.get("/balance/me").then((r) => setBal(r.data)).catch(() => {});
    }
  }, [user]);

  return (
    <div data-testid="topup-page" className="container-pad py-12 md:py-20">
      <div className="overline">// TOP UP CREDIT</div>
      <h1 className="mt-4 font-display text-4xl md:text-6xl tracking-tight max-w-4xl">
        Load your <span className="text-signal">CloudRent balance.</span>
      </h1>
      <p className="mt-4 max-w-2xl text-zinc-400 text-base md:text-lg">
        Add credit to your account once, then activate any VPS or API plan from
        the catalog — billing is deducted daily from your balance. Larger top-ups
        come with bonus credit.
      </p>

      {/* Coming-soon notice */}
      <div
        data-testid="payments-pending-notice"
        className="mt-8 border border-signal/60 bg-zinc-950 p-5 rounded-sm flex items-start gap-3 max-w-3xl"
      >
        <Lock size={16} className="text-signal mt-0.5 shrink-0" />
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-signal">
            Card top-ups temporarily unavailable
          </div>
          <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
            We're finalising our checkout integration. Until self-service card top-ups
            go live, please contact our billing team at{" "}
            <a href="mailto:billing@cloudrentvps.com" className="text-signal hover:underline">
              billing@cloudrentvps.com
            </a>{" "}
            for early access, custom invoicing, or wire transfer. Existing balances
            remain fully usable to activate plans.
          </p>
        </div>
      </div>

      {bal && (
        <div className="mt-6 card-flat p-4 inline-flex items-center gap-6 flex-wrap">
          <div>
            <div className="overline">CURRENT BALANCE</div>
            <div className="font-mono text-2xl text-white">${bal.balance_usd.toFixed(2)}</div>
          </div>
          {bal.daily_burn_usd > 0 && (
            <>
              <div className="h-8 w-px bg-zinc-800 hidden md:block" />
              <div>
                <div className="overline">DAILY BURN</div>
                <div className="font-mono text-lg text-zinc-200">${bal.daily_burn_usd.toFixed(2)}/d</div>
              </div>
              <div className="h-8 w-px bg-zinc-800 hidden md:block" />
              <div>
                <div className="overline">RUNWAY</div>
                <div className="font-mono text-lg text-zinc-200">
                  {bal.runway_days == null ? "—" : `${bal.runway_days} days`}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Preview of package values */}
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-90">
        {packages.map((p) => {
          const active = selected === p.code;
          const isBonus = p.bonus_pct > 0;
          const bonusAmt = p.credit_usd - p.amount_usd;
          return (
            <button
              key={p.code}
              type="button"
              onClick={() => setSelected(p.code)}
              data-testid={`topup-package-${p.code}`}
              className={`card-flat p-6 text-left transition-colors ${
                active ? "border-signal bg-zinc-950" : "hover:border-zinc-600"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="badge-mono">{p.label.toUpperCase()}</span>
                {isBonus && (
                  <span className="badge-mono border-signal/60 text-signal">
                    +{p.bonus_pct}% BONUS
                  </span>
                )}
              </div>
              <div className="mt-5 flex items-baseline gap-1 font-mono">
                <span className="text-zinc-500 text-sm">$</span>
                <span className="text-4xl text-white font-semibold leading-none">
                  {p.amount_usd.toFixed(0)}
                </span>
                <span className="text-zinc-600 text-xs ml-1 uppercase tracking-widest">USD</span>
              </div>
              <div className="mt-2 font-mono text-[11px] text-zinc-500">
                You receive <span className="text-white">${p.credit_usd.toFixed(2)}</span> credit
                {isBonus && (
                  <> · <span className="text-signal">+${bonusAmt.toFixed(2)} bonus</span></>
                )}
              </div>
              <div className="mt-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest">
                <span className={`h-3 w-3 rounded-full border ${active ? "border-signal bg-signal" : "border-zinc-700"} flex items-center justify-center`}>
                  {active && <Check size={9} className="text-black" strokeWidth={3} />}
                </span>
                <span className={active ? "text-white" : "text-zinc-500"}>
                  {active ? "Selected" : "Preview"}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row gap-3 items-start">
        <a
          href="mailto:billing@cloudrentvps.com?subject=Top-up%20Request"
          data-testid="topup-contact-button"
          className="btn-signal h-12 px-6 text-sm font-mono tracking-widest"
        >
          <Mail size={14} /> CONTACT FOR EARLY ACCESS <ArrowUpRight size={12} className="opacity-70" />
        </a>
        <Link to="/catalog" className="btn-ghost h-12 px-6 text-sm font-mono tracking-widest">
          BACK TO CATALOG
        </Link>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-zinc-400">
        <div className="card-flat p-5">
          <div className="overline">// 01 · NO EXPIRY</div>
          <p className="mt-2">Your balance never expires while your account is active.</p>
        </div>
        <div className="card-flat p-5">
          <div className="overline">// 02 · REFUNDABLE</div>
          <p className="mt-2">
            Unused balance can be refunded within 30 days.{" "}
            <Link to="/refund" className="text-signal hover:underline">See refund policy</Link>.
          </p>
        </div>
        <div className="card-flat p-5">
          <div className="overline">// 03 · TRANSPARENT</div>
          <p className="mt-2">Every charge & deduction is logged in your dashboard.</p>
        </div>
      </div>
    </div>
  );
}
